import React, { useEffect, useState } from "react";
import Modal from "../Popups/Modal";
import { createAddress, updateAddress } from "../api/address.api";
import {
  validateAddress,
  clampIntegerInput,
  ADDRESS_LIMITS,
} from "../utils/profileValidation";
import { toast } from "./Toast";
import { apiErrorMessage } from "../utils/apiError";

const ADDRESS_TYPES = ["Home", "Work", "Other"];

const LOCATION_API = "https://countriesnow.space/api/v0.1";

const takenTypes = (addresses = [], current) => {
  const currentKey = (current || "").trim().toLowerCase();
  const used = new Set(
    (addresses || [])
      .map((a) => (a?.addressType || "").trim().toLowerCase())
      .filter((type) => type && type !== currentKey)
  );

  return ADDRESS_TYPES.filter((type) => !used.has(type.toLowerCase()));
};

export const EMPTY_ADDRESS = {
  street: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
  addressType: "",
};

const addressToForm = (address) => ({
  street: address?.street || "",
  city: address?.city || "",
  state: address?.state || "",
  country: address?.country || "",
  postalCode: address?.postalCode || "",
  addressType: address?.addressType || "",
});

const inputClass =
  "w-full min-h-[44px] border border-surface-container rounded-xl px-3 py-2.5 text-sm bg-surface-lowest focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";

const FieldError = ({ errors, name }) =>
  errors[name] ? (
    <p className="text-xs text-error mt-1">{errors[name]}</p>
  ) : null;

async function fetchJson(url, options = {}) {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Location service returned ${response.status}`);
  }

  const result = await response.json();
  if (result?.error) {
    throw new Error(result?.msg || "Unable to load location data.");
  }

  return result;
}

const AddressFormModal = ({
  open,
  onClose,
  staffId,
  editAddress = null,
  onSaved,
  existingAddresses = [],
}) => {
  const [form, setForm] = useState(EMPTY_ADDRESS);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [locationError, setLocationError] = useState("");

  const editMode = Boolean(editAddress?._id);

  // Load all countries when the modal opens.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadCountries = async () => {
      setLoadingCountries(true);
      setLocationError("");

      try {
        const result = await fetchJson(`${LOCATION_API}/countries/iso`);
        if (!cancelled) {
          setCountries(Array.isArray(result?.data) ? result.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setLocationError(
            "Unable to load countries. Please close and reopen the form to try again."
          );
        }
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    };

    loadCountries();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset form on open. In edit mode, also load the saved state's cities.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const initialiseForm = async () => {
      const initial = editAddress ? addressToForm(editAddress) : { ...EMPTY_ADDRESS };
      setForm(initial);
      setFieldErrors({});
      setError("");
      setLocationError("");
      setStates([]);
      setCities([]);

      if (!initial.country) return;

      try {
        setLoadingStates(true);
        const stateResult = await fetchJson(`${LOCATION_API}/countries/states`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: initial.country }),
        });

        const loadedStates = stateResult?.data?.states || [];
        if (!cancelled) setStates(Array.isArray(loadedStates) ? loadedStates : []);

        if (initial.state) {
          setLoadingCities(true);
          const cityResult = await fetchJson(
            `${LOCATION_API}/countries/state/cities`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                country: initial.country,
                state: initial.state,
              }),
            }
          );

          if (!cancelled) {
            setCities(Array.isArray(cityResult?.data) ? cityResult.data : []);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLocationError(
            "Could not load the saved location options. You can select the country, state, and city again."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingStates(false);
          setLoadingCities(false);
        }
      }
    };

    initialiseForm();

    return () => {
      cancelled = true;
    };
  }, [open, editAddress]);

  const onChange = (e) => {
    const { name, value } = e.target;
    let next = value;

    if (name === "postalCode") {
      next = clampIntegerInput(value, { min: 0, max: 999999 }).slice(
        0,
        ADDRESS_LIMITS.postalCode
      );
    } else if (name === "street") {
      next = value.slice(0, ADDRESS_LIMITS.street);
    } else if (name === "addressType") {
      next = value.slice(0, ADDRESS_LIMITS.addressType);
    }

    setForm((current) => ({ ...current, [name]: next }));
    setFieldErrors((previous) => ({ ...previous, [name]: undefined }));
  };

  const handleCountryChange = async (e) => {
    const country = e.target.value;

    setForm((current) => ({ ...current, country, state: "", city: "" }));
    setStates([]);
    setCities([]);
    setFieldErrors((previous) => ({
      ...previous,
      country: undefined,
      state: undefined,
      city: undefined,
    }));
    setLocationError("");

    if (!country) return;

    setLoadingStates(true);
    try {
      const result = await fetchJson(`${LOCATION_API}/countries/states`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country }),
      });
      setStates(Array.isArray(result?.data?.states) ? result.data.states : []);
    } catch (err) {
      setLocationError("Unable to load states for this country. Please try again.");
    } finally {
      setLoadingStates(false);
    }
  };

  const handleStateChange = async (e) => {
    const state = e.target.value;
    const country = form.country;

    setForm((current) => ({ ...current, state, city: "" }));
    setCities([]);
    setFieldErrors((previous) => ({
      ...previous,
      state: undefined,
      city: undefined,
    }));
    setLocationError("");

    if (!country || !state) return;

    setLoadingCities(true);
    try {
      const result = await fetchJson(`${LOCATION_API}/countries/state/cities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country, state }),
      });
      setCities(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      setLocationError("Unable to load cities for this state. Please try again.");
    } finally {
      setLoadingCities(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!staffId) {
      setError("Staff information is missing. Please refresh and try again.");
      return;
    }

    const { valid, errors } = validateAddress(form);
    setFieldErrors(errors);

    if (!valid) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setSaving(true);
    setError("");

    const request = editMode
      ? updateAddress(staffId, editAddress._id, form).then(
          (response) => response.data.updatedAddress
        )
      : createAddress(staffId, form).then((response) => response.data.address);

    request
      .then((address) => {
        onSaved?.(address, editMode ? "update" : "create");
        toast.success(
          editMode ? "Address updated successfully." : "Address added successfully."
        );
        onClose();
      })
      .catch((err) => {
        const serverMessage = apiErrorMessage(err, "");
        const duplicate = /already exists/i.test(serverMessage);
        const message = duplicate
          ? `${serverMessage} Edit the existing address instead.`
          : serverMessage ||
            (editMode ? "Error updating address" : "Error adding address");

        setError(message);
        toast.error(message);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      align="bottom"
      closeSize={24}
      className="max-w-lg w-full rounded-t-2xl sm:rounded-2xl h-[min(92vh,100%)] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col !p-0"
    >
      <div className="px-4 sm:px-6 py-4 border-b border-surface-container shrink-0">
        <h3 className="text-lg font-semibold text-on-primary-fixed-var pr-10">
          {editMode ? "Edit Address" : "Add Address"}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
          {error && <p className="text-sm text-red-500">{error}</p>}
          {locationError && (
            <p className="text-sm text-red-500" role="alert">
              {locationError}
            </p>
          )}

          <div>
            <label className="text-xs text-on-surface-variant">
              Country *
            </label>
            <select
              name="country"
              value={form.country}
              onChange={handleCountryChange}
              disabled={loadingCountries}
              className={`${inputClass} mt-1`}
            >
              <option value="">
                {loadingCountries ? "Loading countries..." : "Select country"}
              </option>
              {countries.map((country) => (
                <option key={country.Iso2 || country.name} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
            <FieldError errors={fieldErrors} name="country" />
          </div>

          <div>
            <label className="text-xs text-on-surface-variant">State *</label>
            <select
              name="state"
              value={form.state}
              onChange={handleStateChange}
              disabled={!form.country || loadingStates}
              className={`${inputClass} mt-1`}
            >
              <option value="">
                {!form.country
                  ? "Select a country first"
                  : "Select state"}
              </option>
              {states.map((state) => {
                const stateName = typeof state === "string" ? state : state.name;
                return (
                  <option key={stateName} value={stateName}>
                    {stateName}
                  </option>
                );
              })}
              {form.state && !states.some(
                (state) =>
                  (typeof state === "string" ? state : state.name) === form.state
              ) && (
                <option value={form.state}>{form.state}</option>
              )}
            </select>
            <FieldError errors={fieldErrors} name="state" />
          </div>

          <div>
            <label className="text-xs text-on-surface-variant">City *</label>
            <select
              name="city"
              value={form.city}
              onChange={onChange}
              disabled={!form.state || loadingCities}
              className={`${inputClass} mt-1`}
            >
              <option value="">
                {!form.state
                  ? "Select a state first"
                  : "Select city"}
              </option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
              {form.city && !cities.includes(form.city) && (
                <option value={form.city}>{form.city}</option>
              )}
            </select>
            <FieldError errors={fieldErrors} name="city" />
          </div>

          <div>
            <label className="text-xs text-on-surface-variant">Street *</label>
            <input
              name="street"
              value={form.street}
              onChange={onChange}
              maxLength={ADDRESS_LIMITS.street}
              className={`${inputClass} mt-1`}
            />
            <FieldError errors={fieldErrors} name="street" />
          </div>

          <div>
            <label className="text-xs text-on-surface-variant">
              Postal Code * (6 digits)
            </label>
            <input
              name="postalCode"
              value={form.postalCode}
              onChange={onChange}
              inputMode="numeric"
              maxLength={ADDRESS_LIMITS.postalCode}
              placeholder="000000"
              className={`${inputClass} mt-1`}
            />
            <FieldError errors={fieldErrors} name="postalCode" />
          </div>

          <div>
            <label className="text-xs text-on-surface-variant">
              Address Type
            </label>
            <select
              name="addressType"
              value={form.addressType}
              onChange={onChange}
              className={`${inputClass} mt-1`}
            >
              <option value="">Select type</option>
              {takenTypes(
                existingAddresses,
                editMode ? editAddress?.addressType : ""
              ).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <FieldError errors={fieldErrors} name="addressType" />
          </div>
        </div>

        <div className="shrink-0 border-t border-surface-container px-4 sm:px-6 py-3 flex gap-3 bg-surface-lowest">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[48px] py-2.5 border border-surface-container rounded-xl text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 min-h-[48px] py-2.5 bg-primary text-white rounded-xl text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddressFormModal;
